import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import { prepareBlueprintForEditor } from "@/features/sparxsEditor/lib/blueprint/blueprintNormalize";
import {
  sparxsEditorBlueprintFetchedHydrated,
  sparxsEditorConflictResolvedWithServerBlueprint,
  sparxsEditorRevertedSessionFileToOriginal,
} from "@/features/sparxsEditor/store/domainActions";

export interface SparxState {
  data: InteractionBlueprintData | null;

  originalData: InteractionBlueprintData | null;
}

type SetSparxFromSessionPayload = {
  data: InteractionBlueprintData;
  originalData: InteractionBlueprintData;
};

const mutateUpdateSparxJson = (
  state: SparxState,
  payload: InteractionBlueprintData | null,
): void => {
  const prepared = payload != null ? prepareBlueprintForEditor(payload) : null;
  state.data = prepared;
  state.originalData = prepared;
};

const mutateSetSparxFromSession = (
  state: SparxState,
  payload: SetSparxFromSessionPayload,
): void => {
  state.data = prepareBlueprintForEditor(payload.data);
  state.originalData = prepareBlueprintForEditor(payload.originalData);
};

const initialState: SparxState = {
  data: null,
  originalData: null,
};

const sparxSlice = createSlice({
  name: "sparx",
  initialState,
  reducers: {
    updateSparxJson: (
      state,
      action: PayloadAction<InteractionBlueprintData | null>,
    ) => {
      mutateUpdateSparxJson(state, action.payload);
    },
    setSparxFromSession: (
      state,
      action: PayloadAction<SetSparxFromSessionPayload>,
    ) => {
      mutateSetSparxFromSession(state, action.payload);
    },
    resetSparxToOriginal: (state) => {
      if (state.originalData != null) {
        state.data = state.originalData;
      }
    },

    clearActiveSparx: (state) => {
      state.data = null;
      state.originalData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sparxsEditorBlueprintFetchedHydrated, (state, action) => {
        mutateUpdateSparxJson(state, action.payload.blueprint);
      })
      .addCase(
        sparxsEditorConflictResolvedWithServerBlueprint,
        (state, action) => {
          const { fileName, activeFileName, blueprint } = action.payload;
          if (activeFileName !== fileName) {
            return;
          }
          mutateSetSparxFromSession(state, {
            data: blueprint,
            originalData: blueprint,
          });
        },
      )
      .addCase(sparxsEditorRevertedSessionFileToOriginal, (state, action) => {
        const { snapshot } = action.payload;
        mutateSetSparxFromSession(state, {
          data: snapshot,
          originalData: snapshot,
        });
      });
  },
});

export const { setSparxFromSession, clearActiveSparx } = sparxSlice.actions;
export default sparxSlice.reducer;
